import React, { useMemo, useState } from "react";

import { faEdit, faTable } from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { isPlainObject } from "lodash-es";
import { validate as validateUUID } from "uuid";

import { GenericObject } from "src/api/flowTypes";
import { DisplayDateTime } from "src/base-components/Date";
import { ExternalLink } from "src/base-components/ExternalLink";
import { MultilineDataValue } from "src/base-components/MultilineDataValue";
import {
  PropertiesList,
  PropertiesListItem,
} from "src/base-components/PropertiesList";
import { Cell, Header, TableComp } from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import {
  getAllKeys,
  resolveColumnType,
} from "src/cases/caseView/blocks/cellTypes";
import { EditableTableBlock } from "src/cases/caseView/blocks/EditableTableBlock";
import {
  TruncatedText,
  TypedCellDisplay,
} from "src/cases/caseView/blocks/TypedCellDisplay";
import { useTableBlockEdits } from "src/cases/caseView/blocks/useTableBlockEdits";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import {
  CaseDeserialized,
  TableBlockMultiColumnRecord,
  TableBlockRecord,
  TableBlockSingleRecord,
} from "src/cases/queries/casesTypes";
import {
  Config1,
  TableColumnConfigOutput,
  TableEditMode,
} from "src/clients/case-management";
import { JSONValueViewer } from "src/dataTable/DetailedView/JSONValueViewer";
import { Button } from "src/design-system/Button";
import { EmptyState } from "src/design-system/EmptyState";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useIsFeatureFlagEnabled } from "src/hooks/useIsFeatureFlagEnabled";
import { FEATURE_FLAGS } from "src/router/featureFlags";
import { useCurrentUserId } from "src/store/AuthStore";
import { isISO8601 } from "src/utils/datetime";
import { speakPythonPrimitive } from "src/utils/speakPython";
import { isProbablyMarkdown, isValidURL } from "src/utils/stringUtils";

type TableBlockData = {
  record: TableBlockRecord;
};

type TableConfig = Extract<Config1, { kind: "table" }>;

type TableBlockProps = Omit<CaseBlockProps, "children"> & {
  data: TableBlockData;
  config?: TableConfig;
  caseData?: CaseDeserialized;
};

const ClampedText: React.FC<{ value: string }> = ({ value }) => (
  <TruncatedText value={value}>
    <div className="line-clamp-3 min-w-0 break-words text-gray-800">
      {value}
    </div>
  </TruncatedText>
);

const LinkValue: React.FC<{ value: string }> = ({ value }) => (
  <TruncatedText value={value}>
    <ExternalLink
      className="min-w-0 truncate hover:underline"
      href={value.startsWith("http") ? value : `https://${value}`}
    >
      {value}
    </ExternalLink>
  </TruncatedText>
);

const renderValue = (value: unknown, key: string): React.ReactNode => {
  if (typeof value === "number") {
    return <span className="font-code-12">{value}</span>;
  }

  if (typeof value === "boolean" || value === null) {
    return speakPythonPrimitive(String(value));
  }

  if (Array.isArray(value) || isPlainObject(value)) {
    return <JSONValueViewer field={key} value={value as GenericObject} />;
  }

  if (typeof value === "string" && isISO8601(value)) {
    return <DisplayDateTime value={value} />;
  }

  if (typeof value === "string" && validateUUID(value)) {
    return <TruncatedIdPill feedback="inline-only-checkmark" id={value} />;
  }

  if (typeof value === "string" && isValidURL(value)) {
    return <LinkValue value={value} />;
  }

  if (typeof value === "string" && isProbablyMarkdown(value)) {
    return <MultilineDataValue fieldName={key} value={value} />;
  }

  if (typeof value === "string") {
    return <ClampedText value={value} />;
  }

  return String(value);
};

const renderCellValue = (
  value: unknown,
  key: string,
  columnsConfig?: Record<string, TableColumnConfigOutput> | null,
): React.ReactNode => {
  if (value === undefined) {
    return null;
  }

  const displayConfig = columnsConfig?.[key]?.display_config ?? null;
  const displayType = resolveColumnType(displayConfig, value);

  return (
    <TypedCellDisplay columnKey={key} displayType={displayType} value={value} />
  );
};

const helper = createColumnHelper<Record<string, unknown>>();

const buildDynamicColumns = (
  keys: string[],
  columnsConfig?: Record<string, TableColumnConfigOutput> | null,
) => {
  return keys.map((key) => {
    const displayName = columnsConfig?.[key]?.display_name || key;
    return helper.accessor((row) => row[key], {
      id: key,
      minSize: 160,
      maxSize: 350,
      header: (info) => (
        <Header dataLoc={`table-block-header-${key}`} info={info}>
          {displayName}
        </Header>
      ),
      cell: (info) => (
        <Cell
          className="truncate"
          dataLoc={`table-block-cell-${key}`}
          info={info}
        >
          {renderCellValue(info.getValue(), key, columnsConfig)}
        </Cell>
      ),
    });
  });
};

const SingleRecordDisplay: React.FC<{
  record: TableBlockSingleRecord;
}> = ({ record }) => {
  const items: PropertiesListItem[] = Object.entries(record).map(
    ([key, value]) => ({
      key,
      label: key,
      value: renderValue(value, key),
    }),
  );

  return (
    <PropertiesList
      dataLocPrefix="table-block"
      items={items}
      rightAlignValues={true}
      showIcons={false}
    />
  );
};

const MultiRecordDisplay: React.FC<{
  records: TableBlockMultiColumnRecord;
  columnsConfig?: Record<string, TableColumnConfigOutput> | null;
}> = ({ records, columnsConfig }) => {
  const columns = useMemo(() => {
    const keys = getAllKeys(records);
    return buildDynamicColumns(keys, columnsConfig);
  }, [records, columnsConfig]);

  return (
    <div className="flex h-full flex-col p-4">
      <div className="min-h-0 flex-1">
        <TableComp
          columns={columns}
          data={records}
          dataLoc="table-block-multi-record"
          disableVirtualization={true}
          frameClassName="h-full"
          getRowId={(_, index) => String(index)}
          rowClassName="hover:bg-gray-50"
          variant="compact"
        />
      </div>
    </div>
  );
};

const isMultiColumnRecord = (
  record: TableBlockRecord,
): record is TableBlockMultiColumnRecord => Array.isArray(record);

export const TableBlock: React.FC<TableBlockProps> = ({
  data,
  config,
  caseData,
  ...props
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const multiColumnEditableEnabled = useIsFeatureFlagEnabled(
    FEATURE_FLAGS.multiColumnTable,
  );
  const { cases } = useCapabilities();
  const currentUserId = useCurrentUserId();

  const isMultiRecord = isMultiColumnRecord(data.record);

  // Check if user can edit blocks: admin or assigned to the case
  const canEditBlock = (() => {
    if (cases.canManage) return true;
    if (!currentUserId) return false;
    const assigneeId = caseData?.data.assignee?.id;
    return cases.canEditBlocks && assigneeId === currentUserId;
  })();

  const isEditable =
    isMultiRecord &&
    multiColumnEditableEnabled &&
    config?.edit_mode === TableEditMode.EDITABLE &&
    canEditBlock;

  const multiRecords = isMultiRecord
    ? (data.record as TableBlockMultiColumnRecord)
    : [];
  const edits = useTableBlockEdits(multiRecords);

  const isEmpty = isMultiRecord
    ? data.record.length === 0
    : Object.keys(data.record).length === 0;

  if (isEmpty) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          description="This table has no data to display"
          headline="No data available"
          icon={faTable}
          size="compact"
        />
      </CaseBlock>
    );
  }

  if (!isMultiColumnRecord(data.record)) {
    return (
      <CaseBlock {...props}>
        <SingleRecordDisplay record={data.record} />
      </CaseBlock>
    );
  }

  const handleCancel = () => {
    edits.resetEdits();
    setIsEditing(false);
  };

  const handleSave = () => {
    // TODO: Wire to save mutation in RES-5659
    setIsEditing(false);
  };

  const editingActions = (
    <div className="flex items-center gap-3">
      <Button
        dataLoc="table-block-cancel-button"
        size="sm"
        variant="secondary"
        onClick={handleCancel}
      >
        Cancel
      </Button>
      <Button
        dataLoc="table-block-save-button"
        size="sm"
        variant="primary"
        onClick={handleSave}
      >
        Save
      </Button>
    </div>
  );

  const editButton = (
    <Button
      dataLoc="table-block-edit-button"
      iconLeft={faEdit}
      size="sm"
      variant="secondary"
      onClick={() => setIsEditing(true)}
    >
      Edit
    </Button>
  );

  const rightSlot = isEditing
    ? editingActions
    : isEditable
      ? editButton
      : undefined;

  return (
    <CaseBlock
      {...props}
      isExpandable={multiColumnEditableEnabled}
      expandedRightSlot={rightSlot}
      noPaddings={true}
      noScroll={true}
      rightSlot={rightSlot}
    >
      {isEditing ? (
        <EditableTableBlock
          columnsConfig={config?.columns}
          defaultColumnsEditMode={config?.default_columns_edit_mode}
          edits={edits}
          records={data.record}
        />
      ) : (
        <MultiRecordDisplay
          columnsConfig={config?.columns}
          records={data.record}
        />
      )}
    </CaseBlock>
  );
};
